import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FaEllipsisV } from "react-icons/fa";
import { BADGE_ICONS } from "../constants/badges";
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("all");
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(7);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [message, setMessage] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [ipHistoryMenu, setIpHistoryMenu] = useState(null);
  const [showReports, setShowReports] = useState({});
  const [showBanDetailsModal, setShowBanDetailsModal] = useState(false);
  const [selectedUserBanDetails, setSelectedUserBanDetails] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [selectedUserForBadges, setSelectedUserForBadges] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState({
    visible: false,
    action: null,
    userId: null,
  });

  const navigate = useNavigate();

  const PERMISSION_TYPES = {
    manage_tokens: {
      label: 'Manage Tokens',
      description: 'Allows managing access tokens',
    },
    manage_requests: {
      label: "Manage Requests",
      description: "Allows user to approve, reject, complete, and delete cheat requests."
    },
    // Add more permissions as needed
  };
  
  // Derived pagination values
  const totalUserPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startUserIndex = (currentUserPage - 1) * usersPerPage;
  const endUserIndex = startUserIndex + usersPerPage;
  const filteredCurrentUsers = filteredUsers.slice(startUserIndex, endUserIndex);

  // Auto-clear messages
  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [message]);

  // Fetch users
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get("/api/admin/users", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUsers(res.data);
      setFilteredUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage({
        text: "Error fetching users.",
        type: "error",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Filter users
  useEffect(() => {
    setFilteredUsers(
      users.filter((user) => {
        const matchesRole = selectedRoleFilter === "all" || user.role === selectedRoleFilter;
        const matchesSearchQuery =
          user.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
          (user.discordUsername && user.discordUsername.toLowerCase().includes(userSearchQuery.toLowerCase())) ||
          (user.email && user.email.toLowerCase().includes(userSearchQuery.toLowerCase())) ||
          (user.ipAddress && user.ipAddress.toLowerCase().includes(userSearchQuery.toLowerCase())) ||
          (user.ipHistory && user.ipHistory.some(ip => ip.toLowerCase().includes(userSearchQuery.toLowerCase())));
        return matchesRole && matchesSearchQuery;
      })
    );
    setCurrentUserPage(1);
  }, [userSearchQuery, users, selectedRoleFilter]);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Helper functions
  const getAvatarUrl = (avatar, username = "User") =>
    avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random&bold=true`;

  const toggleActionMenu = (userId) => {
    setShowActionMenu((prev) => (prev === userId ? null : userId));
  };

  const toggleReportsVisibility = (userId) => {
    setShowReports((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const openBanDetailsModal = (user) => {
    console.log("Opening ban details modal for user:", user.username);
    setSelectedUserBanDetails({
      username: user.username,
      banReason: user.banReason,
      banDate: user.banDate,
      bannedBy: user.bannedBy?.username || "Unknown Admin",
    });
    setShowBanDetailsModal(true);
  };

  const closeBanDetailsModal = () => {
    console.log("Closing ban details modal");
    setShowBanDetailsModal(false);
    setSelectedUserBanDetails(null);
  };

  const verifyUser = async (userId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.post(`/api/admin/users/${userId}/verify`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMessage({
        text: "User verified successfully.",
        type: "success",
      });
      fetchUsers();
    } catch (error) {
      setMessage({
        text: "Error verifying user.",
        type: "error",
      });
      console.error("Error verifying user:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const reverifyUser = async (userId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.post(`/api/admin/users/${userId}/reverify`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMessage({
        text: "User has been set to require email reverification.",
        type: "success",
      });
      fetchUsers();
    } catch (error) {
      setMessage({
        text: "Error requiring user reverification.",
        type: "error",
      });
      console.error("Error requiring reverification:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const reverifyUserWithConfirmation = (userId) => {
    openConfirmationDialog(reverifyUser, userId);
  };


  const changeUserRole = async (userId, newRole) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.put(`/api/admin/users/${userId}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMessage({
        text: "User role updated successfully.",
        type: "success",
      });
      fetchUsers();
    } catch (error) {
      setMessage({
        text: "Error updating user role.",
        type: "error",
      });
      console.error("Error updating user role:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const banUser = async (userId) => {
    try {
      const banReason = prompt("Please enter a reason for banning this user:");
      if (!banReason) {
        setMessage({
          text: "Ban reason is required.",
          type: "error",
        });
        return;
      }
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.post(
        `/api/admin/users/${userId}/ban`,
        { reason: banReason },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setMessage({
        text: "User banned successfully.",
        type: "success",
      });
      fetchUsers();
    } catch (error) {
      setMessage({
        text: "Error banning user.",
        type: "error",
      });
      console.error("Error banning user:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const unbanUser = async (userId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.post(`/api/admin/users/${userId}/unban`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMessage({
        text: "User unbanned successfully.",
        type: "success",
      });
      fetchUsers();
    } catch (error) {
      setMessage({
        text: "Error unbanning user.",
        type: "error",
      });
      console.error("Error unbanning user:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const deleteUser = async (userId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.delete(`/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMessage({
        text: "User deleted successfully.",
        type: "success",
      });
      fetchUsers();
    } catch (error) {
      setMessage({
        text: "Error deleting user.",
        type: "error",
      });
      console.error("Error deleting user:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const removeUserAvatar = async (userId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.delete(`/api/admin/users/${userId}/avatar`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMessage({ text: "Avatar removed successfully.", type: "success" });
      fetchUsers(); // refresh list to show fallback avatar
    } catch (error) {
      setMessage({
        text: error?.response?.data?.message || "Error removing avatar.",
        type: "error",
      });
      console.error("Error removing avatar:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const removeAvatarWithConfirmation = (userId) => {
    openConfirmationDialog(removeUserAvatar, userId);
  };

  const banUserIPs = async (userId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      const user = users.find((u) => u._id === userId);
      if (!user) {
        throw new Error("User not found");
      }
      if (user.role === "admin") {
        setMessage({
          text: `Cannot ban IPs for admin user ${user.username}.`,
          type: "error",
        });
        return;
      }
      const banReason = prompt("Please enter a reason for banning this user's IPs:");
      if (!banReason) {
        setMessage({
          text: "Ban reason is required.",
          type: "error",
        });
        return;
      }
      const ipsToBan = [];
      if (user.ipAddress) {
        ipsToBan.push(user.ipAddress);
      }
      if (user.ipHistory && Array.isArray(user.ipHistory)) {
        user.ipHistory.forEach((ip) => {
          if (!ipsToBan.includes(ip)) {
            ipsToBan.push(ip);
          }
        });
      }
      if (ipsToBan.length === 0) {
        setMessage({
          text: "No IPs found for this user.",
          type: "error",
        });
        return;
      }
      const banPromises = ipsToBan.map((ip) =>
        axios.post(
          "/api/admin/ban-ip",
          {
            ipAddress: ip,
            reason: banReason,
            duration: undefined,
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        )
      );
      await Promise.all(banPromises);
      setMessage({
        text: `Banned ${ipsToBan.length} IPs for user ${user.username}.`,
        type: "success",
      });
    } catch (error) {
      setMessage({
        text: error.response?.data?.error || "Error banning user IPs.",
        type: "error",
      });
      console.error("Error banning user IPs:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const clearReports = async (username) => {
    try {
      const confirmClear = window.confirm(`Are you sure you want to clear all reports for ${username}?`);
      if (!confirmClear) return;
      await axios.post("/api/admin/users/clear-reports", { username });
      setMessage({
        text: "Reports cleared successfully.",
        type: "success",
      });
      fetchUsers();
    } catch (err) {
      console.error("Failed to clear reports:", err);
      setMessage({
        text: "Failed to clear reports. Please try again.",
        type: "error",
      });
    }
  };

  const updateUserBadges = async (userId, badges) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.put(
        `/api/admin/users/${userId}/badges`,
        { badges },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setMessage({
        text: "User badges updated successfully.",
        type: "success",
      });
      fetchUsers();
      setShowBadgeModal(false);
      setSelectedUserForBadges(null);
    } catch (error) {
      setMessage({
        text: "Error updating user badges.",
        type: "error",
      });
      console.error("Error updating user badges:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const updateUserPermissions = async (userId, permissions) => {
    try {
      setActionLoading((prev) => ({ ...prev, [userId]: true }));
      await axios.put(
        `/api/admin/users/${userId}/permissions`,
        { permissions },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setMessage({
        text: "User permissions updated successfully.",
        type: "success",
      });
      fetchUsers();
      setShowPermissionsModal(false);
      setSelectedUserForPermissions(null);
    } catch (error) {
      setMessage({
        text: error.response?.data?.message || "Error updating user permissions.",
        type: "error",
      });
      console.error("Error updating user permissions:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const openConfirmationDialog = (action, userId) => {
    console.log("Opening confirmation dialog for action:", action.name, "userId:", userId);
    setConfirmationDialog({ visible: true, action, userId });
  };

  const closeConfirmationDialog = () => {
    console.log("Closing confirmation dialog");
    setConfirmationDialog({ visible: false, action: null, userId: null });
  };

  const confirmAction = async () => {
    const { action, userId } = confirmationDialog;
    if (action && userId) {
      console.log("Confirming action:", action.name, "for userId:", userId);
      await action(userId);
    }
    closeConfirmationDialog();
  };

  const deleteUserWithConfirmation = (userId) => {
    openConfirmationDialog(deleteUser, userId);
  };

  const banUserWithConfirmation = (userId) => {
    openConfirmationDialog(banUser, userId);
  };

  const unbanUserWithConfirmation = (userId) => {
    openConfirmationDialog(unbanUser, userId);
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu && !event.target.closest(".dropdown-menu")) {
        console.log("Closing action menu due to click outside");
        setShowActionMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionMenu]);

  return (
    <div className="users-container">
      {message && (
        <p className={`adminpanel-${message.type}`}>
          {message.text}
        </p>
      )}
      <div className="role-button-section">
        <h3>Manage Users</h3>
        <div className="user-counts-admin">
          <span>Total: {filteredUsers.length}</span>
          <span>Users: {filteredUsers.filter(user => user.role === "user").length}</span>
          <span>Members: {filteredUsers.filter(user => user.role === "member").length}</span>
        </div>
        <select
          value={selectedRoleFilter}
          onChange={(e) => setSelectedRoleFilter(e.target.value)}
          className="role-filter-admin"
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="moderator">Moderator</option>
          <option value="user">User</option>
          <option value="member">Member</option>
        </select>
      </div>

      <div className="users-per-page-selector">
        <label htmlFor="users-per-page">Users per page:</label>
        <select
          id="users-per-page"
          value={usersPerPage}
          onChange={(e) => {
            setUsersPerPage(Number(e.target.value));
            setCurrentUserPage(1);
          }}
          className="users-per-page-select"
        >
          <option value={5}>5</option>
          <option value={7}>7</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>

      <div className="search-section">
        <input
          type="text"
          placeholder="Search by Username, Discord, IP, or Email..."
          value={userSearchQuery}
          onChange={(e) => setUserSearchQuery(e.target.value)}
          className="user-search-input"
        />
      </div>

      {loadingUsers && <p>Loading users...</p>}
      {filteredUsers.length === 0 && !loadingUsers ? (
        <p>No users found</p>
      ) : (
        <div className="user-list-container">
          {filteredCurrentUsers
            .filter((user) => user)
            .map((user) => (
              <div key={user._id} className="user-card">
                <div className="user-avatar-container">
                  <img
                    src={getAvatarUrl(user.avatar, user.username)}
                    alt={`${user.username}'s Avatar`}
                    className="user-avatar"
                  />
                </div>

                <p>
                  {user.username ? (
                    <Link to={`/profile/${user.username}`} className="profile-link">
                      {user.username}
                    </Link>
                  ) : (
                    "Unknown User"
                  )}
                </p>
                <div className="user-badges">
                  {user.badges && user.badges.length > 0 ? (
                    <div className="badges-container">
                      {user.badges.map((badge) => (
                        BADGE_ICONS[badge] && (
                          <span
                            key={badge}
                            className="badge-icon"
                            title={BADGE_ICONS[badge].tooltip}
                          >
                            {BADGE_ICONS[badge].icon}
                          </span>
                        )
                      ))}
                    </div>
                  ) : (
                    <span className="no-badges">No badges</span>
                  )}
                </div>
                <p>
                  <strong>Email:</strong>{" "}
                  <span style={{ color: user.email ? "#43B581" : "red" }}>
                    {user.email || <FontAwesomeIcon icon={faTimes} />}
                  </span>
                </p>
                <p>
                  <strong>Created:</strong>{" "}
                  <span style={{ color: "#99AAB5", fontStyle: "italic" }}>
                    {new Date(user.createdAt).toLocaleString()}
                  </span>
                </p>
                <p>
                  <strong>ID:</strong>{" "}
                  <span style={{ color: "#99AAB5" }}>{user._id}</span>
                </p>
                <p>
                  <strong>Current IP:</strong>{" "}
                  {user.ipAddress ? user.ipAddress : <FontAwesomeIcon icon={faTimes} style={{ color: "red" }} />}
                </p>
                <p>
                  <strong>Last Login:</strong>{" "}
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : <FontAwesomeIcon icon={faTimes} style={{ color: "red" }} />}
                </p>
                <p>
                  <strong>Verified:</strong>{" "}
                  <span className={user.isVerified ? "text-success" : "text-error"}>
                    {user.isVerified ? "✔" : <FontAwesomeIcon icon={faTimes} style={{ color: "red" }} />}
                  </span>
                </p>
                <p>
                  <strong>Discord:</strong>{" "}
                  {user.discordUsername ? (
                    <span style={{ color: "#7289DA", fontWeight: "bold" }}>{user.discordUsername}</span>
                  ) : (
                    <FontAwesomeIcon icon={faTimes} style={{ color: "red" }} />
                  )}
                </p>
                <p>
                  <span className={`role-badge ${user.role}`}>{user.role}</span>
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <span className={`user-status ${user.banned ? "banned" : "active"}`}>
                    {user.banned ? "Banned" : "Unbanned"}
                  </span>
                  {user.banned && (
                    <button
                      onClick={() => openBanDetailsModal(user)}
                      className="adminpanel-button ban-details"
                      style={{ marginLeft: "10px" }}
                    >
                      View Ban Details
                    </button>
                  )}
                </p>
                <p>
                  <strong>Report Count:</strong>{" "}
                  <span style={{ color: user.reportCount > 0 ? "red" : "#43B581", fontWeight: "bold" }}>
                    {user.reportCount || 0}
                  </span>
                </p>

                <button
                  onClick={() => setIpHistoryMenu((prev) => prev === user._id ? null : user._id)}
                  className="toggle-ip-history-button"
                >
                  {ipHistoryMenu === user._id ? "Hide IP History" : "Show IP History"}
                </button>

                {ipHistoryMenu === user._id && user.ipHistory && Array.isArray(user.ipHistory) && (
                  <div className="ip-history">
                    <h5>IP History (Latest to Oldest):</h5>
                    <ul>
                      {user.ipHistory.length > 0 ? (
                        [...user.ipHistory]
                          .reverse()
                          .map((ip, index) => (
                            <li key={`${ip}-${index}`}>{ip}</li>
                          ))
                      ) : (
                        <li>No IP history available.</li>
                      )}
                    </ul>
                  </div>
                )}

                <button
                  onClick={() => toggleReportsVisibility(user._id)}
                  className="adminpanel-button show-reports"
                >
                  {showReports[user._id] ? "Hide Reports" : "Show Reports"}
                </button>

                {showReports[user._id] && (
                  <div className="reports-list">
                    <h5>User Reports:</h5>
                    <ul>
                      {user.reports && user.reports.length > 0 ? (
                        user.reports.map((report, idx) => (
                          <li key={idx}>
                            <p><strong>Reported By:</strong> {report.reportedBy || "Unknown User"}</p>
                            <p><strong>Reason:</strong> {report.reason}</p>
                            <p><strong>Description:</strong> {report.description || "No description provided"}</p>
                            <p><strong>Reported At:</strong> {new Date(report.date).toLocaleString()}</p>
                          </li>
                        ))
                      ) : (
                        <p>No reports available for this user.</p>
                      )}
                    </ul>
                  </div>
                )}

                <div className="context-menu-container">
                  <button
                    onClick={() => {
                      console.log("Toggling action menu for user:", user._id);
                      setShowActionMenu((prev) => (prev === user._id ? null : user._id));
                    }}
                    className="menu-button"
                  >
                    <FaEllipsisV />
                  </button>
                  {showActionMenu === user._id && (
                    <div className="dropdown-menu">
                      {!user.isVerified && (
                        <button
                          onClick={() => verifyUser(user._id)}
                          disabled={actionLoading[user._id]}
                        >
                          {actionLoading[user._id] ? "Verifying..." : "Verify User"}
                        </button>
                      )}
                      {user.role !== "admin" && (
                        <button onClick={() => changeUserRole(user._id, "admin")}>
                          Set Admin
                        </button>
                      )}
                      {user.role !== "moderator" && (
                        <button onClick={() => changeUserRole(user._id, "moderator")}>
                          Set Moderator
                        </button>
                      )}
                      {user.role !== "developer" && (
                        <button onClick={() => changeUserRole(user._id, "developer")}>
                          Set Developer
                        </button>
                      )}
                      {user.role !== "user" && (
                        <button onClick={() => changeUserRole(user._id, "user")}>
                          Set User
                        </button>
                      )}
                      {user.role !== "member" && (
                        <button onClick={() => changeUserRole(user._id, "member")}>
                          Set Member
                        </button>
                      )}
                      <button
                        onClick={() => {
                          console.log("Opening badges modal for user:", user.username);
                          setSelectedUserForBadges(user);
                          setShowBadgeModal(true);
                          setShowActionMenu(null);
                        }}
                      >
                        Badges
                      </button>
                      <button
                        onClick={() => reverifyUserWithConfirmation(user._id)}
                        disabled={actionLoading[user._id]}
                      >
                        {actionLoading[user._id] ? "Processing..." : "Force Reverify"}
                      </button>
                      <button
                        onClick={() => {
                          console.log("Opening permissions modal for user:", user.username);
                          setSelectedUserForPermissions(user);
                          setShowPermissionsModal(true);
                          setShowActionMenu(null);
                        }}
                      >
                        Permissions
                      </button>
                      <button onClick={() => clearReports(user.username)}>
                        Clear Reports
                      </button>
                      {user.banned ? (
                        <button
                          onClick={() => {
                            console.log("Opening confirmation dialog for unban:", user._id);
                            unbanUserWithConfirmation(user._id);
                          }}
                        >
                          Unban
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            console.log("Opening confirmation dialog for ban:", user._id);
                            banUserWithConfirmation(user._id);
                          }}
                        >
                          Ban
                        </button>
                      )}
                      <button onClick={() => banUserIPs(user._id)}>
                        Ban IPs
                      </button>
                      {user.avatar && (
                        <button
                          onClick={() => {
                            console.log("Opening confirmation dialog for remove avatar:", user._id);
                            removeAvatarWithConfirmation(user._id);
                            setShowActionMenu(null);
                          }}
                          disabled={actionLoading[user._id]}
                        >
                          {actionLoading[user._id] ? "Removing..." : "Remove Avatar"}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          console.log("Opening confirmation dialog for delete:", user._id);
                          deleteUserWithConfirmation(user._id);
                        }}
                      >
                        Delete User
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}

      <div className="pagination-controls">
        <button
          onClick={() => setCurrentUserPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentUserPage === 1}
          className="pagination-button"
        >
          Previous
        </button>
        {currentUserPage > 2 && (
          <>
            <button onClick={() => setCurrentUserPage(1)} className="pagination-button">
              1
            </button>
            {currentUserPage > 3 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        {Array.from({ length: 5 }, (_, i) => currentUserPage - 1 + i).map((page) => {
          if (page > 0 && page <= totalUserPages) {
            return (
              <button
                key={page}
                onClick={() => setCurrentUserPage(page)}
                className={`pagination-button ${currentUserPage === page ? "active" : ""}`}
              >
                {page}
              </button>
            );
          }
          return null;
        })}
        {currentUserPage < totalUserPages - 1 && (
          <>
            {currentUserPage < totalUserPages - 2 && <span className="pagination-ellipsis">...</span>}
            <button onClick={() => setCurrentUserPage(totalUserPages)} className="pagination-button">
              {totalUserPages}
            </button>
          </>
        )}
        <button
          onClick={() => setCurrentUserPage((prev) => Math.min(prev + 1, totalUserPages))}
          disabled={currentUserPage === totalUserPages}
          className="pagination-button"
        >
          Next
        </button>
      </div>

      {confirmationDialog.visible && (
        <div className="confirmation-modal">
          {console.log("Rendering confirmation modal", confirmationDialog)}
          <div className="modal-content">
            <p>Are you sure you want to proceed with this action?</p>
            <div className="modal-button-container">
              <button onClick={confirmAction} className="confirm-button">
                Yes
              </button>
              <button onClick={closeConfirmationDialog} className="cancel-button">
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showBadgeModal && selectedUserForBadges && (
        <div className="confirmation-modal">
          {console.log("Rendering badges modal", selectedUserForBadges)}
          <div className="modal-content">
            <h3>Manage Badges for {selectedUserForBadges.username}</h3>
            <p>Current Badges: {selectedUserForBadges.badges.length > 0 ? selectedUserForBadges.badges.join(", ") : "None"}</p>
            <div className="badge-list">
              {Object.keys(BADGE_ICONS).map((badge) => (
                <div key={badge} className="badge-item">
                  <input
                    type="checkbox"
                    id={`badge-${badge}`}
                    checked={selectedUserForBadges.badges.includes(badge)}
                    onChange={(e) => {
                      const updatedBadges = e.target.checked
                        ? [...selectedUserForBadges.badges, badge]
                        : selectedUserForBadges.badges.filter((b) => b !== badge);
                      setSelectedUserForBadges({
                        ...selectedUserForBadges,
                        badges: updatedBadges,
                      });
                    }}
                  />
                  <label
                    htmlFor={`badge-${badge}`}
                    data-icon={BADGE_ICONS[badge]?.icon || "🏅"}
                  >
                    {badge} ({BADGE_ICONS[badge]?.tooltip || "No description"})
                  </label>
                </div>
              ))}
            </div>
            <div className="modal-button-container">
              <button
                onClick={() => updateUserBadges(selectedUserForBadges._id, selectedUserForBadges.badges)}
                className="confirm-button"
                disabled={actionLoading[selectedUserForBadges._id]}
              >
                {actionLoading[selectedUserForBadges._id] ? "Saving..." : "Save Badges"}
              </button>
              <button
                onClick={() => {
                  console.log("Closing badges modal");
                  setShowBadgeModal(false);
                  setSelectedUserForBadges(null);
                }}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showPermissionsModal && selectedUserForPermissions && (
        <div className="confirmation-modal">
          {console.log("Rendering permissions modal", selectedUserForPermissions)}
          <div className="modal-content">
            <h3>Manage Permissions for {selectedUserForPermissions.username}</h3>
            <p>Current Permissions: {selectedUserForPermissions.permissions?.length > 0 ? selectedUserForPermissions.permissions.join(", ") : "None"}</p>
            <div className="permission-list">
              {Object.keys(PERMISSION_TYPES).map((permission) => (
                <div key={permission} className="permission-item">
                  <input
                    type="checkbox"
                    id={`permission-${permission}`}
                    checked={selectedUserForPermissions.permissions?.includes(permission) || false}
                    onChange={(e) => {
                      const updatedPermissions = e.target.checked
                        ? [...(selectedUserForPermissions.permissions || []), permission]
                        : (selectedUserForPermissions.permissions || []).filter((p) => p !== permission);
                      setSelectedUserForPermissions({
                        ...selectedUserForPermissions,
                        permissions: updatedPermissions,
                      });
                    }}
                  />
                  <label
                    htmlFor={`permission-${permission}`}
                    data-icon="🔑"
                  >
                    {PERMISSION_TYPES[permission].label} ({PERMISSION_TYPES[permission].description})
                  </label>
                </div>
              ))}
            </div>
            <div className="modal-button-container">
              <button
                onClick={() => updateUserPermissions(selectedUserForPermissions._id, selectedUserForPermissions.permissions || [])}
                className="confirm-button"
                disabled={actionLoading[selectedUserForPermissions._id]}
              >
                {actionLoading[selectedUserForPermissions._id] ? "Saving..." : "Save Permissions"}
              </button>
              <button
                onClick={() => {
                  console.log("Closing permissions modal");
                  setShowPermissionsModal(false);
                  setSelectedUserForPermissions(null);
                }}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showBanDetailsModal && selectedUserBanDetails && (
        <div className="confirmation-modal">
          {console.log("Rendering ban details modal", selectedUserBanDetails)}
          <div className="modal-content">
            <h3>Ban Details for {selectedUserBanDetails.username}</h3>
            <p><strong>Reason:</strong> {selectedUserBanDetails.banReason || "No reason provided"}</p>
            <p><strong>Banned On:</strong> {selectedUserBanDetails.banDate ? new Date(selectedUserBanDetails.banDate).toLocaleString() : "Unknown date"}</p>
            <p>
              <strong>Banned By:</strong>{" "}
              {selectedUserBanDetails.bannedBy ? (
                <Link to={`/profile/${selectedUserBanDetails.bannedBy}`} className="profile-link">
                  {selectedUserBanDetails.bannedBy}
                </Link>
              ) : (
                "Unknown Admin"
              )}
            </p>
            <div className="modal-button-container">
              <button onClick={closeBanDetailsModal} className="cancel-button">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;